<template>
  <div
    :class="dense ? '' : 'page-container'"
    :style="dense ? `height: ${height}` : ''">
    <v-dialog
      v-if="!customNewPreset"
      max-width="500px"
      v-model="new_preset_dialog">
      <NewPreset @close="new_preset_dialog = false"></NewPreset>
    </v-dialog>
    <div
      style="display: flex"
      :class="dense ? '' : 'mb-4 mt-2 mx-5'">
      <input
        :accept="props.filter !== 'presets' ? '.vza,.orb' : '.cpre'"
        type="file"
        style="display: none"
        multiple
        id="template_file_upload"
        @input="send_to_upload_queue()" />
      <v-btn
        v-if="!dense"
        style="align-items: center; height: 40px !important"
        variant="flat"
        color="var(--v-cardColor-base)"
        class="px-2 d-flex align-center mr-3 subtleshadow top-bar-buttons"
        @click="open_file_dialog()"
        title="Upload Template">
        <v-icon>mdi-upload-outline</v-icon>
      </v-btn>
      <v-btn
        v-if="props.filter === 'presets' && !dense"
        style="align-items: center"
        variant="flat"
        height="40px !important"
        color="var(--v-cardColor-base)"
        class="px-2 d-flex align-center mr-2 subtleshadow top-bar-buttons"
        @click="
          new_preset_dialog = true;
          emit('newPresetDialog');
        "
        title="Create a New Preset">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <v-spacer v-if="!dense" />
      <v-text-field
        v-if="showSearchBar"
        v-model="search"
        density="compact"
        append-inner-icon="mdi-magnify"
        single-line
        class="subtleshadow"
        hide-details
        variant="solo"
        :placeholder="`Search ${filter}...`"
        @input="user_search(searchQueryUpdated)"
        rounded
        autofocus
        style="border-radius: 5px !important; max-width: 880px"
        bg-color="var(--v-cardColor-base) !important"
        color="var(--text) !important"></v-text-field>
      <v-select
        v-if="showSearchBar"
        label="Sort By"
        class="subtleshadow ml-2"
        density="compact"
        variant="solo"
        hide-details
        item-title="display_name"
        item-value="value"
        return-object
        rounded
        v-model="sortBy"
        style="border-radius: 5px !important; max-width: 160px"
        bg-color="var(--v-cardColor-base) !important"
        color="var(--text) !important"
        :items="[
          { display_name: 'Time Created', value: 'timestamp' },
          { display_name: 'Name', value: 'name' },
        ]"></v-select>
      <v-btn
        v-if="showSearchBar"
        style="align-items: center"
        variant="flat"
        height="40px"
        color="var(--v-cardColor-base)"
        class="px-2 d-flex align-center ml-2 subtleshadow top-bar-buttons"
        @click="
          order = order === 'asc' ? 'desc' : 'asc';
          searchQueryUpdated();
        "
        :title="
          order === 'asc'
            ? 'Change to Sort Descending'
            : 'Change to Sort Ascending'
        ">
        <v-icon v-if="order === 'asc'">mdi-sort-ascending</v-icon>
        <v-icon v-if="order === 'desc'">mdi-sort-descending</v-icon>
      </v-btn>
      <v-btn
        v-if="showSearchBar && !dense && !selectMode"
        variant="flat"
        height="40px"
        color="var(--v-cardColor-base)"
        class="ml-2 subtleshadow top-bar-buttons"
        :loading="selectAllLoading"
        @click="handleSelectAllByFilter"
        title="Select All">
        <v-icon>mdi-select-all</v-icon>
      </v-btn>
      <v-progress-linear
        v-if="!dense"
        :active="waitForLoad"
        :model-value="loadNum"
        color="primary"
        absolute
        height="4"></v-progress-linear>
    </div>

    <!-- Selection Banner (shows when non-visible items are selected) -->
    <SelectionBanner
      v-if="!dense && !selectMode"
      :total-selected="filterSelection.selectedUids.value.length"
      :visible-count="filterSelection.visibleSelectedCount.value"
      :non-visible-count="filterSelection.nonVisibleSelectedCount.value"
      item-type="templates"
      @clear="handleClearFilterSelection" />

    <!-- Bulk Delete Confirmation Dialog -->
    <BulkConfirmDialog
      :open="bulkDeleteDialogOpen"
      title="Delete Selected Templates"
      :message="`Are you sure you want to delete ${selected_templates.length} template(s)?`"
      confirm-label="Delete"
      confirm-color="error"
      icon="mdi-trash-can"
      icon-color="error"
      :total-count="selected_templates.length"
      :non-visible-count="filterSelection.nonVisibleSelectedCount.value"
      item-type="templates"
      :loading="multi_delete_loading"
      @close="bulkDeleteDialogOpen = false"
      @confirm="confirmBulkDelete" />

    <transition name="slide-fade">
      <div
        v-if="selected_templates.length > 0 && !dense"
        style="
          position: fixed;
          height: 100px;
          z-index: 99;
          bottom: 0px;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          justify-content: center;
        ">
        <v-card
          flat
          style="width: 220px; height: 100px; border-radius: 4px"
          class="subtleshadow pa-3">
          <div
            class="mb-2"
            style="display: flex; justify-content: center; z-index: 999999">
            <v-icon class="mr-2">mdi-image-multiple-outline</v-icon>
            {{ selected_templates.length }} Templates Selected
          </div>
          <div style="display: flex; justify-content: center">
            <v-btn
              variant="tonal"
              class="mr-3"
              @click="handleClearFilterSelection">
              <v-icon>mdi-cancel</v-icon>
            </v-btn>
            <v-btn
              @click="handleBulkDeleteWithConfirmation()"
              :loading="multi_delete_loading"
              class="mr-2"
              style="background-color: var(--inner-background)"
              variant="flat">
              <v-icon>mdi-trash-can</v-icon>
            </v-btn>
          </div>
        </v-card>
      </div>
    </transition>
    <div
      class="templates-wrapper"
      :style="'max-height: ' + containerHeight">
      <TagsViewer
        :showTags="showTags"
        :dense="dense"
        @select="searchQueryUpdated()"
        :tags="deluge.tags"
        :filterByTag="selected_filters" />
      <v-infinite-scroll
        :height="dense ? '100%' : `calc(100vh - 240px)`"
        @load="loadMore">
        <v-virtual-scroll
          :height="dense ? '100%' : `calc(100vh - 240px)`"
          width="100%"
          :item-height="dense ? 97 : 110"
          :renderless="true"
          :items="
            Object.values(templates.templates).sort((a, b) => {
              return a.index < b.index;
            })
          ">
          <template v-slot:default="{ item }">
            <WideCard
              :dense="dense"
              @click="
                selectMode
                  ? assetSelectModeClick(item)
                  : asset_click_handle(item)
              "
              :class="dense ? 'mr-2' : 'mx-5'"
              :tag="true"
              :style="item.status === 0 ? 'opacity:0.5;' : ''"
              :title="item.display_name"
              :prefixtitle="generateTemplateTypeChips(item)"
              :selected="selected_templates.includes(item.uid)"
              :icon="item.icon"
              :icons="generateIcons(item)"
              :img="resolveTemplateThumbnail(item)"
              tooltip="Open Template Details">
              <Tag
                v-if="item.src_uid"
                icon="mdi-cloud-sync-outline"
                name="Omni"
                color="#4a5899" />
              <Tag
                v-for="(tag, i) in item.tags"
                :key="(tag, i)"
                :color="deluge.tags?.[tag]?.color"
                :icon="deluge.tags?.[tag]?.icon"
                :name="deluge.tags?.[tag]?.name" />
            </WideCard>
          </template>
        </v-virtual-scroll>
      </v-infinite-scroll>
    </div>
    <v-dialog
      v-if="templateEditable"
      v-model="open_template_modal"
      max-width="600px"
      class="template-pop-up"
      :persistent="editModalLock">
      <EditTemplateModal
        :template="template_index"
        :itemtype="'template'"
        v-if="open_template_modal && template_index"
        v-on:close="if (!editModalLock) open_template_modal = false;"
        @locked="(bool) => (editModalLock = bool)" />
    </v-dialog>
  </div>
</template>
<script setup>
import NewPreset from './NewPreset.vue';
import { onMounted, ref, watch } from 'vue';
import { user_search, loadNum, waitForLoad } from '../../utils/pseudoLoader.js';
import { deluge, state } from '@/stores/deluge.js';

import WideCard from '../Shared/WideCard.vue';
import EditTemplateModal from './EditTemplateModal.vue';
import Tag from '../Shared/Tag.vue';
import { $http } from '../../stores/http.js';
import TagsViewer from '../Shared/TagsViewer.vue';
import { applyPatch } from 'rfc6902';
import { selectWithModifiers } from '@/utils/selectionRange.js';
import { assetWideThumbnail, getThumbnail } from '../../utils/thumbnails.js';
import SelectionBanner from '../Shared/SelectionBanner.vue';
import BulkConfirmDialog from '../Shared/BulkConfirmDialog.vue';
import { useFilterSelection } from '../../composables/useFilterSelection.js';
const emit = defineEmits([
  'selected',
  'newPresetDialog',
  'results-count',
  'loading',
]);
const ctrl_down = ref(false);
const shift_down = ref(false);

watch(
  () => state.ctrl_down,
  (newVal) => {
    ctrl_down.value = newVal;
  }
);
watch(
  () => state.shift_down,
  (newVal) => {
    shift_down.value = newVal;
  }
);

// Filter selection composable for "Select All by Filter" functionality
const filterSelection = useFilterSelection('templates');
const bulkDeleteDialogOpen = ref(false);
const selectAllLoading = ref(false);

const props = defineProps({
  filter: {
    type: String,
    default: 'templates',
  },
  dense: {
    type: Boolean,
    default: false,
  },
  height: {
    type: String,
    default: '100%',
  },
  selectMode: {
    type: Boolean,
    default: false,
  },
  selectMultiple: {
    type: Boolean,
    default: true,
  },
  showTags: {
    type: Function,
  },
  showSearchBar: {
    type: Boolean,
    default: true,
  },
  searchQuery: {
    type: String,
    default: '',
  },
  returnObject: {
    type: Boolean,
    default: false,
  },
  tagQuery: {
    type: Set,
    default: () => new Set(),
  },
  excludeUids: {
    type: Array,
    default: () => [],
  },
  containerHeight: {
    type: String,
    default: 'calc(105vh - 240px)',
  },
  srcFileFilter: {
    type: String,
    default: '',
  },
  templateEditable: {
    type: Boolean,
    default: true,
  },
  prefixTitleEnabled: {
    type: Boolean,
    default: true,
  },
  customNewPreset: {
    type: Boolean,
    default: false,
  },
  customPrefix: {
    type: Function,
    default: null,
  },
  prefixColor: {
    type: String,
    default: null,
  },
  prefixBackgroundColor: {
    type: String,
    default: null,
  },
});

watch(
  () => props.searchQuery,
  (newVal) => {
    search.value = newVal;
    user_search(searchQueryUpdated);
  }
);
watch(
  () => props.tagQuery,
  (newVal) => {
    selected_filters.value = Array.from(newVal);
    searchQueryUpdated();
  }
);

const selected_filters = ref([]);
async function searchQueryUpdated() {
  // Emit loading state
  emit('loading', true);

  const new_templates = JSON.parse(
    await $http(
      'GET',
      `/get_templates?limit=30&offset=0&search_query=${
        search.value
      }&filter_tags=${selected_filters.value.join(',')}&preset=${
        props.filter
      }&sort_by=${sortBy.value.value}_${
        order.value
      }&exclude_uids=${props.excludeUids.join(',')}&src_file_filter=${
        props.srcFileFilter
      }`
    )
  );
  templates.value.templates = new_templates;

  // Emit results count and loading complete
  emit('results-count', Object.keys(new_templates).length);
  emit('loading', false);
}
function assetSelectModeClick(item) {
  // Support ctrl-click and shift-click range selection when in select mode
  const result = selectWithModifiers({
    itemsMap: templates.value.templates,
    selected: selected_templates.value,
    clickedUid: item.uid,
    ctrlDown: ctrl_down.value,
    shiftDown: shift_down.value,
    shiftAnchor: shift_click_started,
    selectMultiple: props.selectMultiple,
  });

  selected_templates.value = result.selected;
  shift_click_started = result.shiftAnchor;

  if (!props.returnObject) {
    emit('selected', selected_templates.value); // return uid
  } else {
    emit(
      'selected',
      selected_templates.value.map((uid) => templates.value.templates[uid])
    );
  }
}
function pruneMismatch(templatesCopy) {
  const searchValue = search.value.toLowerCase(); // Ensure case-insensitive comparison
  for (const template of Object.values(templatesCopy.templates)) {
    let match = false;

    // Check if the template's name includes the search value
    const nameMatches = template.name.toLowerCase().includes(searchValue);

    // Check if any of the tags match the selected filters
    if (selected_filters.value.length > 0) {
      for (const tag of template.tags) {
        if (selected_filters.value.includes(tag)) {
          match = true;
          break;
        }
      }
    } else {
      match = true; // If no filters are selected, consider it a match
    }

    // Remove the template if it doesn't match the name or the filters
    if (!nameMatches || !match) {
      delete templatesCopy.templates[template.uid];
    }
  }
}
async function loadMore(resp) {
  const new_templates = JSON.parse(
    await $http(
      'GET',
      `/get_templates?limit=30&offset=${
        Object.values(templates.value.templates).length
      }&search_query=${search.value}&filter_tags=${selected_filters.value.join(
        ','
      )}&preset=${props.filter}&sort_by=${sortBy.value.value}_${
        order.value
      }&exclude_uids=${props.excludeUids.join(',')}&src_file_filter=${
        props.srcFileFilter
      }`
    )
  );
  templates.value.templates = {
    ...templates.value.templates,
    ...new_templates,
  };
  if (Object.values(new_templates).length === 0) {
    resp.done('empty');
  } else {
    resp.done();
  }
}
watch(
  () => deluge.patch,
  () => {
    if (deluge.patch.key === 'templates') {
      console.log('Applying template patch', deluge.patch.data);
      applyPatch(templates.value, deluge.patch.data);

      // Handle new templates (add operations)
      let patchedTemplates = Object.values(deluge.patch.data)
        .filter((patch) => patch.op === 'add')
        .map((patch) => {
          return patch.value;
        });

      // Add new templates at the top
      templates.value.templates = {
        ...Object.fromEntries(
          patchedTemplates.map((template) => [template.uid, template])
        ),
        ...templates.value.templates,
      };

      // Re-assign indices to all templates to ensure consistent behavior
      const templateArray = Object.values(templates.value.templates);
      for (let i = 0; i < templateArray.length; i++) {
        templateArray[i].index = templateArray.length - i - 1; // Reverse order to keep newest at top
      }
    }
  }
);

const sortBy = ref({ display_name: 'Time Created', value: 'timestamp' });
const order = ref('desc');
const new_preset_dialog = ref(false);
const template_index = ref('');
const search = ref(props.searchQuery || '');
const open_template_modal = ref(false);
const templates = ref({ templates: {} });
watch(sortBy, () => {
  searchQueryUpdated();
});

/**
 * Generate template type chips for display before the title
 * Returns array of strings: [".VZA", ".ORB"] based on template support
 */
function generateTemplateTypeChips(templateItem) {
  const template_types = templateItem.extra_info?.template_type || ['vza'];
  const chips = [];

  if (template_types.includes('vza')) {
    chips.push('.VZA');
  }
  if (template_types.includes('orb')) {
    chips.push('.ORB');
  }

  return chips;
}

function generateIcons(templateItem) {
  const icons = [];
  if (templateItem.dynamic_preload) {
    icons.push('mdi-feather');
  }
  if (!templateItem.multi) {
    icons.push('mdi-swap-horizontal-bold');
  }
  if (templateItem.extra_info?.nested_template) {
    icons.push('mdi-chart-sankey-variant');
  }
  if (templateItem.extra_info?.cue_take_startup) {
    icons.push('mdi-one-up');
  }
  if (templateItem.conditions?.play_conditions?.length > 0) {
    icons.push('mdi-motion-play-outline');
  }
  if (templateItem.extra_info?.ignore_empty) {
    icons.push('mdi-sitemap-outline');
  }
  return icons;
}
const editModalLock = ref(false);

const selected_templates = ref([]);
const multi_delete_loading = ref(false);
let shift_click_started = false;
function resolveTemplateThumbnail(template) {
  return getThumbnail(template);
}
function asset_click_handle(template) {
  emit('selected', template);
  if (!ctrl_down.value && !shift_down.value) {
    template_index.value = template;
    open_template_modal.value = true;
  } else if (ctrl_down.value) {
    if (!selected_templates.value.includes(template.uid)) {
      selected_templates.value.push(template.uid);
      shift_click_started = template.uid;
    } else {
      selected_templates.value = selected_templates.value.filter(
        function (item) {
          return item !== template.uid;
        }
      );
      if (selected_templates.value.length === 0) {
        shift_click_started = false;
      }
    }
  } else if (shift_down.value) {
    if (shift_click_started) {
      // select templates within range
      const start = templates.value.templates[shift_click_started].index;
      const end = template.index;
      const template_list = Object.values(templates.value.templates);
      for (let i = 0; i < template_list.length; i++) {
        if (
          (template_list[i].index >= start && template_list[i].index <= end) ||
          (template_list[i].index <= start && template_list[i].index >= end)
        ) {
          if (!selected_templates.value.includes(template_list[i].uid)) {
            selected_templates.value.push(template_list[i].uid);
          }
        }
      }
      shift_click_started = false;
    }
    if (!selected_templates.value.includes(template.uid)) {
      selected_templates.value.push(template.uid);
    }
    shift_click_started = template.uid;
  }
}
async function multi_delete() {
  multi_delete_loading.value = true;
  const templates = [...selected_templates.value];

  try {
    await $http(
      'DELETE',
      '/v2/delete_template_multi',
      JSON.stringify(templates)
    );
  } catch (e) {
    console.log('Error deleting templates', e);
  }

  selected_templates.value = [];
  filterSelection.clearSelection();
  multi_delete_loading.value = false;
  shift_click_started = false;
}

function send_to_upload_queue() {
  const file_input = document.getElementById('template_file_upload');
  for (const file of file_input.files) {
    state.upload_files.push(file);
  }
  file_input.files = new DataTransfer().files;
}
function open_file_dialog() {
  const file_input = document.getElementById('template_file_upload');
  file_input.click();
}

// Filter Selection Functions

/**
 * Handle "Select All by Filter" button click.
 * Fetches all UIDs matching current filter and selects them.
 */
async function handleSelectAllByFilter() {
  selectAllLoading.value = true;
  try {
    await filterSelection.selectAllByFilter({
      filter_tags: selected_filters.value.join(','),
      search_query: search.value,
      preset: props.filter,
      src_file_filter: props.srcFileFilter,
    });
    // Sync filter selection to selected_templates for compatibility with existing code
    selected_templates.value = [...filterSelection.selectedUids.value];
  } finally {
    selectAllLoading.value = false;
  }
}

/**
 * Clear filter selection and reset to manual mode.
 */
function handleClearFilterSelection() {
  filterSelection.clearSelection();
  selected_templates.value = [];
  shift_click_started = false;
}

/**
 * Open bulk delete confirmation when using filter selection mode.
 * Always confirm when selecting via "Select All" to prevent accidental mass deletion.
 */
function handleBulkDeleteWithConfirmation() {
  if (filterSelection.selectionMode.value === 'filter') {
    bulkDeleteDialogOpen.value = true;
  } else {
    multi_delete();
  }
}

/**
 * Confirm and execute bulk delete.
 */
async function confirmBulkDelete() {
  await multi_delete();
  bulkDeleteDialogOpen.value = false;
  filterSelection.clearSelection();
}

/**
 * Update visible UIDs when templates load/change.
 */
function updateVisibleUidsFromTemplates() {
  const visible = Object.keys(templates.value?.templates || {});
  filterSelection.updateVisibleUids(visible);
}

// Watch templates changes to update visible UIDs
watch(
  () => templates.value?.templates,
  () => {
    updateVisibleUidsFromTemplates();
  },
  { deep: true }
);
</script>
<style scoped>
.templates-wrapper {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(105vh - 240px);
}

.top-bar-buttons {
  height: 40px !important;
}
.v-infinite-scroll__side {
  display: none !important;
}
</style>
